/* Shared formatters used across pages.
   Centralizes date/currency/percentage formatting so we don't duplicate
   helper functions in every page component. */

const WEEKDAYS_PT = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

/** "2025-04-24" -> "24/04/2025"; null/undefined -> "—". */
export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}

/** ISO datetime "2025-04-24T15:42:00Z" → "24/04/2025 12:42" (locale pt-BR). */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return iso;
  }
}

/** "2025-04-24" -> "24/04/2025 - quinta-feira". */
export function formatBRDate(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso + "T00:00:00");
    return `${fmtDate(iso)} - ${WEEKDAYS_PT[dt.getDay()]}`;
  } catch {
    return iso;
  }
}

/** Number → "R$ 1.234,56". `value=null` returns "—". */
export function fmtBRL(value, { fallback = "—" } = {}) {
  if (value === null || value === undefined || isNaN(Number(value))) return fallback;
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

/** Safe currency that returns "R$ 0,00" instead of "—" when value is missing.
 * Use in dashboards/cards where always showing a value is preferable to a dash. */
export function safeBRL(value) {
  return fmtBRL(value, { fallback: "R$ 0,00" });
}

/** Locale-formatted integer/decimal. Always returns a string, never crashes.
 * @param value any (null/undefined/NaN → defaults to 0)
 * @param options.maximumFractionDigits default 0 (integer style)
 * @param options.minimumFractionDigits default 0 */
export function safeNumber(value, { maximumFractionDigits = 0, minimumFractionDigits = 0 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return (0).toLocaleString("pt-BR");
  return n.toLocaleString("pt-BR", { maximumFractionDigits, minimumFractionDigits });
}

/** Safe percentage display. `value` is expected as PRE-MULTIPLIED percentage (e.g., 22.34 for 22.34%).
 * For fractions in [0..1], use fmtPct instead.
 * Returns "0%" for null/undefined/NaN. */
export function safePctDisplay(value, { precision = 1 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toLocaleString("pt-BR", { maximumFractionDigits: precision })}%`;
}

/** 0.123 → "12,3%"; precision is digits after decimal. */
export function fmtPct(value, { precision = 1, fallback = "—" } = {}) {
  if (value === null || value === undefined || isNaN(Number(value))) return fallback;
  return `${(Number(value) * 100).toFixed(precision).replace(".", ",")}%`;
}

/** "2025-04-24" → "Apr 2025"-style "abril/2025" Brazilian month. */
export function fmtMonthYear(iso) {
  if (!iso) return "";
  const [y, m] = iso.substring(0, 7).split("-");
  const months = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  const mi = parseInt(m, 10) - 1;
  return `${months[mi] || m}/${y}`;
}
